import apiFetch from '@wordpress/api-fetch';

const BASE_URL_KEY = 'epi.baseUrl';
const TOKEN_KEY = 'epi.supportToken';
const TOKEN_EXP_KEY = 'epi.supportTokenExpiresAt';
const SUPPORT_TOKEN_ENDPOINT = '/epi/v1/support-token';

async function getBase() {
    const fromLS = localStorage.getItem(BASE_URL_KEY);
    if (fromLS) return fromLS;

    const res = await apiFetch({path: '/epi/v1/base-url', method: 'GET'});
    const base = typeof res === 'string' ? res : res?.baseUrl;
    if (base) localStorage.setItem(BASE_URL_KEY, base);
    return base;
}

function decodeJwtPayload(token) {
    if (!token || typeof token !== 'string') return null;

    const parts = token.split('.');
    if (parts.length < 2) return null;

    try {

        const base64Url = parts[1];
        const base64 = base64Url
            .replace(/-/g, '+')
            .replace(/_/g, '/')
            .padEnd(base64Url.length + (4 - (base64Url.length % 4)) % 4, '=');

        const json = atob(base64);
        return JSON.parse(json);
    } catch (e) {
        return null;
    }
}

async function getSupportToken() {
    const now = Date.now();
    const cachedToken = localStorage.getItem(TOKEN_KEY);
    const expRaw = localStorage.getItem(TOKEN_EXP_KEY);
    const expMs = expRaw ? parseInt(expRaw, 10) : 0;

    if (cachedToken && expMs && expMs - now > 60 * 1000) {
        return cachedToken;
    }

    const res = await apiFetch({path: SUPPORT_TOKEN_ENDPOINT, method: 'GET'});

    const token = res?.token || null;
    const expiresIn = typeof res?.expires_in === 'number' ? res.expires_in : null;

    if (!token) {
        return cachedToken || '';
    }

    const payload = decodeJwtPayload(token);
    let jwtExpMs = null;

    if (payload && typeof payload.exp === 'number') {

        jwtExpMs = payload.exp * 1000;
    }

    let expiresAt;

    if (jwtExpMs && jwtExpMs > now) {

        expiresAt = jwtExpMs - 60 * 1000;
    } else if (expiresIn && expiresIn > 0) {

        expiresAt = now + Math.max(30, expiresIn) * 1000;
    } else {

        expiresAt = now + 5 * 60 * 1000;
    }

    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_EXP_KEY, String(expiresAt));

    return token;
}

function clearSupportToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXP_KEY);
}

export function installApiFetchBridge({
                                          stripApiPrefix = false,
                                      } = {}) {
    apiFetch.use(async (options, next) => {
        if (options.url && /^https?:\/\//i.test(options.url)) {
            return next(options);
        }

        if (options.path && options.path.startsWith('/api')) {
            const base = await getBase();

            const suffix = stripApiPrefix
                ? options.path.replace(/^\/api/, '')
                : options.path;
            const url = `${base}${suffix}`;

            const {path, ...rest} = options;

            const makeRequestWithAuth = async () => {
                const headers = {...(rest.headers || {})};
                const token = await getSupportToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;

                const response = await next({
                    ...rest,
                    url,
                    headers,
                    parse: false,
                });

                if (!response.ok) {
                    const text = await response.text();
                    let json;
                    try {
                        json = JSON.parse(text);
                    } catch {
                        json = null;
                    }

                    const error = new Error('External API request failed');
                    error.status = response.status;
                    error.bodyText = text;
                    error.bodyJson = json;
                    throw error;
                }

                return response.json();
            };

            try {
                return await makeRequestWithAuth();
            } catch (err) {
                const status =
                    err?.data?.status ??
                    err?.status ??
                    err?.response?.status ??
                    null;

                if (status !== 401) {
                    throw err;
                }

                clearSupportToken();

                try {
                    return await makeRequestWithAuth();
                } catch (retryErr) {
                    throw retryErr;
                }
            }
        }

        return next(options);
    });
}